import { Box } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useState, useEffect, useMemo } from 'react';
import { AffiliateCampaignReport } from '../../../@types/api';
import { appendTotalsRow, getTotalsRowClass } from '../../../helpers';
import { defaultColumnsAffiliateCampaignReports } from '../../../helpers/table';
import { usePagination } from '../../../hooks/usePagination';
import useSort from '../../../hooks/useSort';
import { createColumn, formatSnakeCaseString } from '../../../utils';
import { createDateColumn, createNumberColumn } from '../../../helpers/columns';
import DateRangeSelect, { DayjsTimestamp } from '../../molecules/DateRangeSelect';
import TableTemplate from '../../templates/TableTemplate';
import { dayjs } from '../../../config/dayjs';
import API_ROUTES from '../../../constants/apiRoutes';
import { COLUMN_FIELDS } from '../../../constants/table';

const styles = {
  container: {
    position: 'relative',
    height: 'calc(100% - 200px)',
  },
};

const AffiliateCampaignReportsPage = () => {
  const [date, setDate] = useState<DayjsTimestamp>({
    fromTimestamp: dayjs().startOf('day'),
    toTimestamp: dayjs().endOf('day'),
  });

  const { data, updateQueryParams, isFetching, isLoading, changeQuery } = usePagination<AffiliateCampaignReport>(
    API_ROUTES.REPORTS.AFFILIATE_CAMPAIGN,
    {
      page: 1,
      limit: 25,
      fromTimestamp: date.fromTimestamp.valueOf(),
      toTimestamp: date.toTimestamp.valueOf(),
    },
    undefined,
    'crm'
  );

  const { handleSort } = useSort(changeQuery);

  const handleSearch = (value: string | null) => {
    changeQuery({ search: value });
  };

  const handleDateChange = (newDate: DayjsTimestamp) => {
    setDate(newDate);
  };

  useEffect(() => {
    changeQuery({
      fromTimestamp: date.fromTimestamp.valueOf(),
      toTimestamp: date.toTimestamp.valueOf(),
    });
  }, [date, changeQuery]);

  const columns: GridColDef[] = useMemo(
    () => [
      createColumn(COLUMN_FIELDS.ID, 'ID', { sortable: false }),
      createColumn(COLUMN_FIELDS.NAME, 'Campaign Name', { alwaysVisible: true }),
      createColumn(COLUMN_FIELDS.TYPE, 'Campaign Type', {
        sortable: false,
        valueFormatter: (value, row, column, apiRef) => formatSnakeCaseString(value),
      }),
      createDateColumn(COLUMN_FIELDS.CREATED_AT, 'Created At'),
      createColumn(COLUMN_FIELDS.PRODUCT, 'Product', {
        sortable: false,
        valueFormatter: (value, row, column, apiRef) => formatSnakeCaseString(value),
      }),
      createColumn(COLUMN_FIELDS.STATUS, 'Status', {
        valueFormatter: (value, row, column, apiRef) => formatSnakeCaseString(value),
      }),
      createColumn(COLUMN_FIELDS.CLICKS, 'Clicks'),
      createColumn(COLUMN_FIELDS.UNIQUE_CLICKS, 'Unique Clicks'),
      createColumn(COLUMN_FIELDS.REGISTRATIONS, 'Registrations'),
      createColumn(COLUMN_FIELDS.FIRST_DEPOSITS, 'First Deposits', { sortable: false }),
      createNumberColumn(COLUMN_FIELDS.FIRST_DEPOSIT_AMOUNT, 'First Deposit Amount', { sortable: false }),
      createNumberColumn(COLUMN_FIELDS.WAGERING_SPORTSBOOK, 'Wagering Sportsbook', { sortable: false }),
      createNumberColumn(COLUMN_FIELDS.WAGERING_CASINO, 'Wagering Casino', { sortable: false }),
      createNumberColumn(COLUMN_FIELDS.GGR, 'GGR', { sortable: false }),
      createNumberColumn(COLUMN_FIELDS.NGR, 'NGR', { sortable: false }),
      createColumn(COLUMN_FIELDS.ACTIVE_PLAYERS, 'Active Players', { sortable: false }),
      createNumberColumn(COLUMN_FIELDS.COMMISSION_EARNED, 'Commission Earned', { sortable: false }),
      createNumberColumn(COLUMN_FIELDS.CONVERSION_RATE, 'Conversion Rate (%)', { sortable: false }),
      createNumberColumn(COLUMN_FIELDS.EARNINGS_PER_CLICK, 'Earnings Per Unique Click', { sortable: false }),
    ],
    []
  );

  const aggregatedData = appendTotalsRow(data?.items || [], data?.totals as AffiliateCampaignReport | undefined);

  return (
    <Box sx={styles.container}>
      <TableTemplate
        rows={aggregatedData || []}
        columns={columns}
        loading={isFetching || isLoading}
        rowCount={data?.count || 0}
        handlePaginationModelChange={updateQueryParams}
        handleSearch={handleSearch}
        handleSort={handleSort}
        changeQuery={changeQuery}
        getRowClassName={getTotalsRowClass}
        defaultVisibleColumns={defaultColumnsAffiliateCampaignReports}
        showExport
        customTableHeaderContent={<DateRangeSelect date={date} onDateChange={handleDateChange} />}
      />
    </Box>
  );
};

export default AffiliateCampaignReportsPage;
